import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getApiClient } from 'api';
import { postPosts } from 'api/generated';
import type { PostPostsResponse } from 'api/generated';
import { errorToastMessage, successToastMessage } from 'helpers/toaster';
import { postCreated } from 'store/feed/feedSlice';
import type { NewPostParamsType, PostType } from 'types/post';

export const createPost = createAsyncThunk<PostPostsResponse, NewPostParamsType & { callBack?: () => void }>(
  'posts/createPost',
  async ({ content, profile, sharedPostId, asAdmin, images, video, callBack }, { rejectWithValue, dispatch }) => {
    try {
      // Validate required content
      if (!content?.trim()) {
        content = '';
      }

      const response = await postPosts({
        client: getApiClient(false),
        body: {
          content: content.trim(),
          masqueraded_id: profile.id,
          masqueraded_type: profile.type,
          shared_post_id: sharedPostId,
          as_tennistalker: asAdmin,
          images:
            images?.map((img) => ({
              id: img.id,
              url: img.url as string,
              _destroy: img._destroy,
            })) || [],
          video: video,
        },
        throwOnError: true,
      });

      // Handle success - dispatch actions and return response
      dispatch(postCreated(response.data.post as PostType));
      dispatch(clearNewPost());
      setTimeout(() => {
        if (callBack) callBack();
      }, 2000);
      return response.data;
    } catch (error) {
      // Handle error - reject with error
      return rejectWithValue(error);
    }
  },
);

export const newPostSlice = createSlice({
  name: 'newPost',
  initialState: {
    error: false,
    loading: false,
    sharedPost: null as PostType | null,
    content: '',
  },
  reducers: {
    sharePost: (state, action) => {
      state.sharedPost = action.payload;
    },
    setNewPost: (state, action) => {
      state.content = action.payload;
    },
    clearNewPost: (state) => {
      state.content = '';
      state.sharedPost = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createPost.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(createPost.fulfilled, (state) => {
      state.loading = false;
      successToastMessage('Il tuo post è stato pubblicato.');
    });
    builder.addCase(createPost.rejected, (state) => {
      state.loading = false;
      state.error = true;
      errorToastMessage('Si è verificato un errore nella creazione del post.');
    });
  },
});

export const { sharePost, setNewPost, clearNewPost } = newPostSlice.actions;
export const newPostReducer = newPostSlice.reducer;
